/**
 * Offer-selection confirm handler for the calendar-reserve flow shared by the inventory list and
 * detail pages. Takes setters for the resolved-offer and picker-open state; returns the async
 * `onConfirm` for `QuickAddOfferPicker`.
 */
import * as React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useCreateOffer } from '../../offers/api/mutations';
import { useSetLastUsedOfferDraftMutation, markOfferAsLastUsedDraft } from './api/user-preferences';
import { buildCreateOfferPayload } from './lib/build-create-offer-payload';
import { mapOfferCreationForbiddenError } from './lib/map-offer-creation-forbidden-error';
import type {
  QuickAddOfferPickerConfirmArgs,
  QuickAddOfferPickerConfirmCreationFailedResult,
  QuickAddOfferPickerConfirmResult,
} from './quick-add-offer-picker';

/**
 * Distinct from the outer catch in `handleCalendarPickerConfirm` below
 * (losing edit access to an *existing* offer, where falling back to the
 * draft list is the right recovery step): a 403 here means the offer was
 * never created (e.g. a sales-department caller lacking
 * offer.assign_other_salesperson) -- stay in 'creating' with the caller's
 * typed values intact instead of resetting, mirroring
 * use-quick-add-pipeline's handleQuickAddCreationError
 * (offer-client-collaboration, CU-86cbc2qv8). Rethrows for any other error
 * so the outer catch's existing recover-forbidden handling still applies.
 */
function handleCalendarCreationError(
  error: unknown,
  t: TFunction,
): QuickAddOfferPickerConfirmCreationFailedResult {
  const result = mapOfferCreationForbiddenError(error, t);
  if (result) return result;
  throw error;
}

/**
 * A resolved offer target for the calendar click-to-reserve flow. Originally
 * defined in the retired `CalendarReserveSelectionPopover` (see findings.md
 * "two-popover path retirement leaves 4 files as orphaned dead code"); moved
 * here since this hook is the only remaining live consumer.
 */
export interface ResolvedOffer {
  id: number;
  title: string;
}

interface UseCalendarOfferPickerOptions {
  setCalendarResolvedOffer: (offer: ResolvedOffer | null) => void;
  closeCalendarPicker: () => void;
}

export function useCalendarOfferPicker({
  setCalendarResolvedOffer,
  closeCalendarPicker,
}: UseCalendarOfferPickerOptions) {
  const createOffer = useCreateOffer();
  const setLastUsedOfferDraft = useSetLastUsedOfferDraftMutation();
  const { t } = useTranslation(['common']);

  const handleCalendarPickerConfirm = React.useCallback(
    async (args: QuickAddOfferPickerConfirmArgs): Promise<QuickAddOfferPickerConfirmResult> => {
      try {
        if (args.mode === 'creating') {
          let created: Awaited<ReturnType<typeof createOffer.mutateAsync>>;
          try {
            created = await createOffer.mutateAsync(buildCreateOfferPayload(args));
          } catch (error) {
            return handleCalendarCreationError(error, t);
          }
          const offerId = created.id != null ? String(created.id) : null;
          if (!offerId) return { status: 'recover-forbidden' as const };
          setCalendarResolvedOffer({ id: Number(offerId), title: created.title ?? '' });
          markOfferAsLastUsedDraft(offerId, args.isLockedTarget, setLastUsedOfferDraft);
          closeCalendarPicker();
          return { status: 'success' as const, offerId };
        }
        const offerId = args.offerId;
        if (!offerId) return { status: 'recover-forbidden' as const };
        setCalendarResolvedOffer({ id: Number(offerId), title: args.offerName ?? offerId });
        closeCalendarPicker();
        return { status: 'success' as const, offerId };
      } catch {
        return { status: 'recover-forbidden' as const };
      }
    },
    [closeCalendarPicker, createOffer, setCalendarResolvedOffer, setLastUsedOfferDraft, t],
  );

  return { handleCalendarPickerConfirm, createOffer };
}
